import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {APPROVAL_STATUS} from '../../../constants';
import {INDICATOR_COLOR} from '../../new-ui/HeaderLabel';

export const getTotalWeekTime = (timeRegistrations, firstDayOfWeek) => {
	let totalWeekTime = 0;
	let totalWeekBillableTime = 0;

	for (let i = 0; i < 7; i++) {
		const day = firstDayOfWeek.clone().add(i, 'day');

		totalWeekTime += timeRegistrations.reduce((sum, t) => {
			if (t.node.day === day.date()) {
				return t.node.minutesRegistered + sum;
			}
			return sum;
		}, 0);

		totalWeekBillableTime += timeRegistrations.reduce((sum, t) => {
			if (t.node.day === day.date()) {
				return t.node.billableMinutesRegistered + sum;
			}
			return sum;
		}, 0);
	}

	return {totalWeekTime, totalWeekBillableTime};
};

export const hasNoAccessToEditProjectTimeReg = (actualPersonId, dayRegNodes) => {
	const containsProjectTimeRegFromOtherPerson = dayRegNodes.some(
		item => (item.node.project || item.node.task) && item.node.person.id !== actualPersonId
	);
	return containsProjectTimeRegFromOtherPerson && !hasPermission(PERMISSION_TYPE.TIME_REGISTRATION_UPDATE_ALL);
};

export const hasNoAccessToTimeRegProject = timeReg => {
	return !!(
		timeReg?.task?.project?.fullAccessToProject === false ||
		timeReg?.project?.fullAccessToProject === false ||
		timeReg?.project?.readOnlyAccess === true ||
		timeReg?.task?.project?.readOnlyAccess === true
	);
};

// Lower rank number = higher display priority. The aggregate status is the one with the lowest rank across all relevant regs.
// PENDING (rank 2) takes priority over NOT_SUBMITTED (rank 3) because it is the intended replacement draft state —
// a row containing both surfaces PENDING as the more actionable state for the user.
const STATUS_RANK = {
	[APPROVAL_STATUS.DECLINED]: 0,
	[APPROVAL_STATUS.SUBMITTED]: 1,
	[APPROVAL_STATUS.PENDING]: 2,
	[APPROVAL_STATUS.NOT_SUBMITTED]: 3,
	[APPROVAL_STATUS.APPROVED]: 4,
	[APPROVAL_STATUS.INVOICED]: 5,
};

export const getTimesheetEntriesApprovalStatus = (
	timeRegs,
	{usingTimeApproval = false, usingInternalTimeApproval = false, usingTimeOffApproval = false} = {}
) => {
	const relevantTimeRegs = timeRegs.filter(tReg => {
		if (!tReg.node.idleTime) return usingTimeApproval;
		if (tReg.node.idleTime.isInternalTime) return usingInternalTimeApproval;
		return usingTimeOffApproval;
	});

	// No regs of any enabled approval type — caller renders no badge instead of misleading "Not submitted".
	if (relevantTimeRegs.length === 0) return null;

	return relevantTimeRegs.reduce((worst, tReg) => {
		const status = tReg.node.approvalStatus ?? APPROVAL_STATUS.PENDING;
		const rank = STATUS_RANK[status] ?? STATUS_RANK[APPROVAL_STATUS.PENDING];
		return rank < STATUS_RANK[worst] ? status : worst;
	}, APPROVAL_STATUS.INVOICED);
};

export const getApprovalStatusBadge = status => {
	switch (status) {
		case APPROVAL_STATUS.APPROVED:
			return {color: INDICATOR_COLOR.GREEN, labelId: 'common.approved'};
		case APPROVAL_STATUS.SUBMITTED:
			return {color: INDICATOR_COLOR.BLUE, labelId: 'time_approval.submitted'};
		case APPROVAL_STATUS.PENDING:
			return {color: INDICATOR_COLOR.AMBER, labelId: 'time_approval.pending'};
		case APPROVAL_STATUS.DECLINED:
			return {color: INDICATOR_COLOR.RED, labelId: 'time_approval.declined'};
		case APPROVAL_STATUS.INVOICED:
			return {color: INDICATOR_COLOR.GREY, labelId: 'common.invoiced'};
		case APPROVAL_STATUS.NOT_SUBMITTED:
			return {color: INDICATOR_COLOR.AMBER, labelId: 'time_approval.not_submitted'};
		default:
			return null;
	}
};
